import {useConfig} from '@aboutyou/config';
import {BodyTypesSection as BodyTypesSectionType} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation_page/sections.types';
import {
  originClick,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions, useTrackOriginClick} from '@aboutyou/tracking';
import {
  CardImage,
  CardImageContent,
  FONT_SIZES,
  FONT_WEIGHTS,
  HorizontalSlider,
  HorizontalSliderHeader,
  mobileOnlyLinaria,
  RowSection,
  SliderPaginationBase,
  tabletAndUpLinaria,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {TextBanner} from '../../../features/Recommendations/shared';

interface BodyTypesSectionListProps extends BodyTypesSectionType {
  streamPosition: number;
}

export const BodyTypesSection: React.FC<BodyTypesSectionListProps> = ({
  streamPosition,
  caption,
  headline,
  elements,
}) => {
  const {device} = useConfig();
  const trackOriginClick = useTrackOriginClick();

  const onCardClick = (locationId?: string) => {
    if (locationId) {
      trackOriginClick(locationId as originClick)();
    }
  };

  const Wrapper =
    device === 'desktop' ? BodyTypesSectionList : BodyTypesSectionGrid;

  return (
    <RowSection data-testid="bodyTypesSection">
      <Wrapper
        headline={headline}
        caption={caption}
        streamPosition={streamPosition}
      >
        {elements.map((element, index) => (
          <StyledTrackPromotions
            key={index}
            Comp="li"
            streamPosition={streamPosition}
            promotion={{
              source: TrackingPromotionCreative.GenericPromotion,
              promotionTracker: element.promotionTracker,
            }}
            triggerOnce
          >
            <StyledCardImage
              data-testid="bodyTypesSectionCard"
              image={element.coverImage}
              imageRatio="1.3:1"
              breakpoints={[{columns: 2}]}
              cta={{url: element.link.url}}
              onClick={() => onCardClick(element.promotionTracker.locationId)}
            >
              <Headline color="Text.White">{element.headline}</Headline>
            </StyledCardImage>
          </StyledTrackPromotions>
        ))}
      </Wrapper>
    </RowSection>
  );
};

const BodyTypesSectionList: React.FC<
  React.PropsWithChildren<Omit<BodyTypesSectionListProps, 'elements'>>
> = ({children, headline, caption, streamPosition}) => {
  return (
    <StyledHorizontalSlider
      dataTestId="bodyTypesSection"
      uniqueKey={`bodyTypesSection-${streamPosition}`}
      hideSideButtons
      constrained
      disableHeaderControl
      SlideControlComponent={SliderPaginationBase}
      title={<StyledTextBanner headline={headline} subline={caption} />}
      slideCount={2.2}
      slideCountSmall={2.5}
      slideCountMedium={4}
      enableGridLayout
    >
      {children}
    </StyledHorizontalSlider>
  );
};

const BodyTypesSectionGrid: React.FC<
  React.PropsWithChildren<Omit<BodyTypesSectionListProps, 'elements'>>
> = ({children, headline, caption}) => {
  return (
    <>
      <StyledTextBanner headline={headline} subline={caption} />
      <GridWrapper data-testid="bodyTypesSection">{children}</GridWrapper>
    </>
  );
};

const StyledTextBanner = styled(TextBanner)`
  ${mobileOnlyLinaria(`margin-left: 16px;`)}
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-inline: 16px;
`;

const StyledHorizontalSlider = styled(HorizontalSlider)`
  ${tabletAndUpLinaria(`
    ul {
      padding-left: 0;
      margin-top: 24px;
    }
  `)}

  ${HorizontalSliderHeader} {
    margin: 0;
  }
`;

const StyledTrackPromotions = styled(TrackPromotions)`
  list-style: none;
`;

const StyledCardImage = styled(CardImage)`
  ${CardImageContent} {
    padding: 8px;
  }
`;

const Headline = styled(Text)`
  font-weight: ${FONT_WEIGHTS.w400};

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px16};
    font-weight: ${FONT_WEIGHTS.w500};
  `)}
`;
